import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import es from "./locales/es.json";
import nl from "./locales/nl.json";
import ar from "./locales/ar.json";
import { detectGeo } from "@/lib/geo";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "ar", label: "العربية", flag: "🇲🇦" },
] as const;

const LS_KEY = "bwt:lang";
const SUPPORTED = ["en", "fr", "es", "nl", "ar"] as const;
const RTL_LANGS = new Set(["ar"]);

function applyDir(lng: string) {
  try {
    document.documentElement.dir = RTL_LANGS.has(lng) ? "rtl" : "ltr";
  } catch { /* ignore */ }
}

if (!i18n.isInitialized) {
  // IMPORTANT: init synchronously in English on BOTH server and client so the
  // first render matches and avoids a hydration mismatch. We swap to the
  // user's chosen / detected language after mount via applyDetectedLanguage().
  i18n
    .use(initReactI18next)
    .init({
      lng: "en",
      resources: {
        en: { translation: en },
        fr: { translation: fr },
        es: { translation: es },
        nl: { translation: nl },
        ar: { translation: ar },
      },
      fallbackLng: "en",
      supportedLngs: SUPPORTED as unknown as string[],
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
}

export function applyDetectedLanguage() {
  if (typeof window === "undefined") return;
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored && SUPPORTED.includes(stored as typeof SUPPORTED[number])) {
      if (i18n.language !== stored) i18n.changeLanguage(stored);
      try { document.documentElement.lang = stored; } catch { /* ignore */ }
      applyDir(stored);
      return;
    }
    const navLang = (navigator.language || "en").slice(0, 2);
    if (SUPPORTED.includes(navLang as typeof SUPPORTED[number])) {
      i18n.changeLanguage(navLang);
      try { localStorage.setItem(LS_KEY, navLang); } catch { /* ignore */ }
      try { document.documentElement.lang = navLang; } catch { /* ignore */ }
      applyDir(navLang);
      return;
    }
    detectGeo().then((g) => {
      const lang = SUPPORTED.includes(g.language as typeof SUPPORTED[number]) ? g.language : "en";
      i18n.changeLanguage(lang);
      try { localStorage.setItem(LS_KEY, lang); } catch { /* ignore */ }
      try { document.documentElement.lang = lang; } catch { /* ignore */ }
      applyDir(lang);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

if (typeof window !== "undefined") {
  i18n.on("languageChanged", (lng) => {
    try { document.documentElement.lang = lng; } catch { /* ignore */ }
    try { localStorage.setItem(LS_KEY, lng); } catch { /* ignore */ }
    applyDir(lng);
  });
}

export default i18n;
